import React, { useEffect, useState } from 'react';
import { Button } from 'rebass';
import { Checkbox, Input, Label } from '@rebass/forms';
import { FiAlertTriangle } from 'react-icons/fi';

import helpers from 'utils/helpers';
import NiceModal from 'components/NiceModal';
import NotificationCard from 'components/NotificationCard';
import { useForm } from 'hooks/useForm';
import { useKeyValidator } from 'hooks/useKeyValidator';
import { useOnPasteBlocker } from 'hooks/useOnPasteBlocker';

/**
 * Modal for permanently deleting study participants.
 *
 * Surfaces the destructive nature of hard delete (screener responses, uploaded files,
 * session history are lost) and gates the action behind a type-in confirmation.
 *
 * When the backend rejects the request with `error_code: scheduled_or_paid_participants`,
 * the parent sets `requiresForce` to true and the modal reveals a force checkbox plus
 * an additional warning. The user must check the box AND re-type the confirmation to
 * retry with `force: true`.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Function} props.onConfirm - Called with `{ force }` when the user submits
 * @param {boolean} props.isSubmitting
 * @param {boolean} props.requiresForce - When true, show force checkbox + scheduled/paid warning
 * @param {number} props.participantCount - For display in the modal title/copy
 */
export function DeleteStudyPeopleModal({
    isOpen,
    onClose,
    onConfirm,
    isSubmitting = false,
    requiresForce = false,
    participantCount = 0
}) {
    const { register, handleSubmit, reset, watch } = useForm();
    const watchedKey = watch('key');
    const noun = participantCount === 1 ? 'participant' : 'participants';
    const countLabel = `${participantCount} ${noun}`;
    const lockPhrase = `delete ${countLabel}`;
    const isKeyValid = useKeyValidator(watchedKey, lockPhrase);
    const blockOnPaste = useOnPasteBlocker();

    const [forceChecked, setForceChecked] = useState(false);

    const onSubmit = handleSubmit(() => {
        if (!helpers.validateKey(watchedKey, lockPhrase)) return;
        if (requiresForce && !forceChecked) return;
        onConfirm({ force: requiresForce });
    });

    const onCancel = () => {
        reset();
        setForceChecked(false);
        onClose();
    };

    // Clear typed confirmation + force checkbox whenever the modal closes, so the next
    // open (for a different participant set) starts fresh. The parent can close us by
    // flipping `isOpen` without going through `onCancel` (e.g., after a successful
    // submit), so resetting in `onCancel` alone is not enough.
    //
    // `reset` is intentionally omitted from deps — `useForm`'s `reset` is not memoized,
    // so including it would re-run this effect every render and wipe state mid-typing.
    useEffect(() => {
        if (!isOpen) {
            reset();
            setForceChecked(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const title = `${requiresForce ? 'Force Delete' : 'Delete'} ${countLabel}`;
    const primaryDisabled = !isKeyValid || (requiresForce && !forceChecked) || isSubmitting;

    return (
        <NiceModal isOpen={isOpen} shouldCloseOnOverlayClick onRequestClose={onCancel} title={title} overflow="visible">
            <p className="color-text-secondary mb-4">
                Permanently removes <span className="medium">{countLabel}</span> from this study. Their screener
                responses, uploaded files, and session history will be destroyed, and this study will no longer appear
                in the panelist's Past Participation.
            </p>
            <p className="color-text-secondary mb-4">
                Panelists stay in your panel — only their participation in this study is removed. Incentive history and
                payment audit records are preserved.
            </p>

            <form id="delete-study-people-form" onSubmit={onSubmit}>
                <p className="mb-2">
                    To continue, type "<span className="medium">{lockPhrase}</span>":
                </p>
                <Input
                    {...register('key')}
                    onPaste={blockOnPaste}
                    autoFocus
                    autoComplete="off"
                    placeholder={lockPhrase}
                />
            </form>

            {requiresForce && (
                <NotificationCard
                    className="!mt-4"
                    type="warn"
                    icon={<FiAlertTriangle />}
                    title="Some are scheduled or already paid."
                    content={
                        <>
                            Any incentives already paid will <span className="medium">not</span> be refunded, scheduled
                            sessions will <span className="medium">not</span> be cancelled on calendars, and the
                            participants will <span className="medium">not</span> be told they were removed. You'll need
                            to handle those yourself if needed.
                        </>
                    }
                    extra={
                        <Label
                            htmlFor="force-delete-acknowledge"
                            className="flex items-center cursor-pointer !mb-0 mt-4"
                        >
                            <Checkbox
                                id="force-delete-acknowledge"
                                name="force-delete-acknowledge"
                                checked={forceChecked}
                                onChange={e => setForceChecked(e.target.checked)}
                            />
                            <span>I understand, force delete</span>
                        </Label>
                    }
                />
            )}

            <div className="modal-actions">
                <Button type="button" onClick={onCancel} variant="secondary-gray" className="!mr-2">
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant="danger-filled"
                    disabled={primaryDisabled}
                    className="modal-primary !m-0"
                    form="delete-study-people-form"
                >
                    {title}
                </Button>
            </div>
        </NiceModal>
    );
}
